// ✅ src/pages/VehicleV2/HireVehicleBillApprovalHOListing.jsx  (UPDATED FULL FILE)
// Mount assumed:
//   app.use("/api/vehicleBillApproval", vehicleBillApproval);
//
// Backend endpoints used:
//   GET  /api/vehicleBillApproval/init?loginUserId=&branchId=
//   GET  /api/vehicleBillApproval/list?branchId=&vehicleNo=&vehicleMainTypeId=&storeId=&page=&pageSize=
//   GET  /api/vehicleBillApproval/autocomplete/vehicleNo?prefix=&branchId=
//   POST /api/vehicleBillApproval/approve
//   POST /api/vehicleBillApproval/disapprove
//
// ✅ Updated pattern based on BillGenerationListing style:
// - common localStorage / sessionStorage header pattern
// - useMemo + useCallback pattern
// - pageLoading + dropdownLoading + gridLoading + actionLoading states
// - cleaner filter / list / autocomplete handling
// - consistent card / section / button style
// - safer refresh / init / pagination flow
// - fixed branchId param bug in list params
// - fixed pagination stale-state issue
// - in-app confirm dialog instead of window.confirm

import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  FaSearch,
  FaEye,
  FaArrowLeft,
  FaFilter,
  FaSyncAlt,
  FaUndo,
  FaCheckCircle,
  FaTimesCircle,
  FaTimes,
  FaExclamationTriangle,
} from "react-icons/fa";

const API_BASE = "https://nissi-india.com/api/vehicleBillApproval";

/* ---------------- helpers ---------------- */
const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const toStr = (v, def = "") => {
  if (v === null || v === undefined) return def;
  return String(v).trim();
};

const clean = (v) => String(v ?? "").trim();

const normalizeOptions = (arr) => {
  if (!Array.isArray(arr)) return [];
  if (arr.length === 0) return [];
  if (typeof arr[0] === "string") {
    return arr.map((s) => ({ id: s, name: s }));
  }

  return arr.map((x) => ({
    id:
      x.id ??
      x.value ??
      x.key ??
      x.fld_id ??
      x.fld_branchid ??
      x.fld_storeid ??
      x.fld_vehicletypeid,
    name:
      x.name ??
      x.label ??
      x.text ??
      x.fld_name ??
      x.fld_branchname ??
      x.fld_storename ??
      x.VehicleType ??
      x.fld_vehicletype,
  }));
};

const defaultFiltersFactory = (defaultBranchId) => ({
  branchId: defaultBranchId,
  vehicleNo: "",
  vehicleMainTypeId: "Select",
  storeId: "Select",
});

const ConfirmDialog = ({
  open,
  isDark,
  title,
  message,
  confirmText,
  cancelText,
  danger,
  confirmClass,
  ghostClass,
  busy,
  onCancel,
  onConfirm,
}) => {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={busy ? undefined : onCancel}
      />
      <div
        className={`relative w-full max-w-md rounded-2xl shadow-2xl border p-6 ${
          isDark
            ? "bg-gradient-to-br from-gray-800 to-gray-900 border-white/10 text-white"
            : "bg-white border-gray-200 text-gray-900"
        }`}
      >
        <div className="flex items-start gap-3 mb-4">
          <div
            className={`mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
              danger
                ? isDark
                  ? "bg-red-900/40 text-red-300"
                  : "bg-red-100 text-red-600"
                : isDark
                  ? "bg-green-900/40 text-green-300"
                  : "bg-green-100 text-green-700"
            }`}
          >
            {danger ? <FaTimesCircle /> : <FaCheckCircle />}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>
              {title}
            </h3>
            <p className={`mt-2 text-sm leading-6 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              {message}
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className={`p-2 rounded-lg ${
              isDark ? "hover:bg-gray-700 text-gray-300" : "hover:bg-gray-100 text-gray-500"
            }`}
            aria-label="Close"
          >
            <FaTimes />
          </button>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button type="button" onClick={onCancel} disabled={busy} className={ghostClass}>
            {cancelText || "Cancel"}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className={`${confirmClass} ${busy ? "opacity-60 cursor-not-allowed" : ""}`}
          >
            {confirmText || "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
};

const VehicleBillApproval = () => {
  const navigate = useNavigate();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  // ✅ shared localStorage / sessionStorage pattern
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchIdFromStorage =
    localStorage.getItem("branchId") ||
    localStorage.getItem("Branchid") ||
    localStorage.getItem("branchid") ||
    "0";
  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "HO";
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "0";
  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "0";

  const defaultBranchId = toInt(branchIdFromStorage, 0);

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchIdFromStorage,
      "x-branch": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchIdFromStorage, branchType, userId]
  );

  /* ---------------- state ---------------- */
  const [pageLoading, setPageLoading] = useState(false);
  const [dropdownLoading, setDropdownLoading] = useState(false);
  const [gridLoading, setGridLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const [showFilters, setShowFilters] = useState(true);

  const [branches, setBranches] = useState([{ id: "Select", name: "Select" }]);
  const [vehicleMainTypes, setVehicleMainTypes] = useState([
    { id: "Select", name: "Select" },
  ]);
  const [stores, setStores] = useState([{ id: "Select", name: "Select" }]);

  const [filters, setFilters] = useState(defaultFiltersFactory(defaultBranchId));

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // billId -> { approve, disapprove }
  const [checks, setChecks] = useState({});

  // vehicle no autocomplete
  const [vehNoSuggestions, setVehNoSuggestions] = useState([]);
  const [showVehNoSuggestions, setShowVehNoSuggestions] = useState(false);

  const [confirmDlg, setConfirmDlg] = useState({
    open: false,
    kind: "",
    title: "",
    message: "",
    confirmText: "Confirm",
    danger: false,
    items: [],
  });

  const sugBoxRef = useRef(null);
  const vehNoRef = useRef(null);
  const autocompleteTimer = useRef(null);

  const isBusy = pageLoading || dropdownLoading || gridLoading || actionLoading;

  /* ---------------- styles ---------------- */
  const cardClass = useMemo(
    () =>
      `p-6 mt-6 rounded-2xl shadow-xl border ${
        isDark
          ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
          : `bg-white ${currentSkin.glass} text-black`
      }`,
    [isDark, currentSkin]
  );

  const sectionClass = useMemo(
    () =>
      `rounded-xl p-6 border ${
        isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
      }`,
    [isDark]
  );

  const inputClass = useMemo(
    () =>
      `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
        isDark
          ? "bg-gray-700/30 border-gray-600 text-white placeholder:text-gray-400"
          : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
      }`,
    [isDark]
  );

  const labelClass = useMemo(
    () =>
      `block text-sm font-medium mb-2 ${
        isDark ? "text-gray-300" : "text-gray-700"
      }`,
    [isDark]
  );

  const pillClass = useMemo(
    () =>
      `inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
        isDark ? "bg-blue-900/60 text-blue-100" : "bg-blue-100 text-blue-800"
      }`,
    [isDark]
  );

  const suggestionBoxClass = useMemo(
    () =>
      `absolute z-20 mt-1 w-full rounded-lg border shadow-lg max-h-56 overflow-auto ${
        isDark ? "bg-gray-900 border-gray-700" : "bg-white border-gray-200"
      }`,
    [isDark]
  );

  const ghostBtn = useMemo(
    () =>
      `flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
        isDark
          ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
          : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
      }`,
    [isDark]
  );

  /* ---------------- helpers ---------------- */
  const canUse = useCallback((cond, msg) => {
    if (!cond) toast.error(msg);
    return !!cond;
  }, []);

  const closeConfirm = useCallback(() => {
    if (isBusy) return;
    setConfirmDlg((p) => ({ ...p, open: false, kind: "", items: [] }));
  }, [isBusy]);

  const resetChecks = useCallback((list = []) => {
    const map = {};
    for (const r of list) {
      const id = toInt(r.fld_billid, 0);
      const approved = !!r.fld_IsApproved;
      map[id] = {
        approve: approved,
        disapprove: !approved,
      };
    }
    setChecks(map);
  }, []);

  const resetSuggestions = useCallback(() => {
    setVehNoSuggestions([]);
    setShowVehNoSuggestions(false);
  }, []);

  const resetFiltersOnly = useCallback(() => {
    setFilters(defaultFiltersFactory(defaultBranchId));
    resetSuggestions();
  }, [defaultBranchId, resetSuggestions]);

  const buildListParams = useCallback(
    (override = {}, pageOverride = page, pageSizeOverride = pageSize) => ({
      branchId:
        (override.branchId ?? filters.branchId) &&
        (override.branchId ?? filters.branchId) !== "Select"
          ? override.branchId ?? filters.branchId
          : "",
      vehicleNo: toStr(override.vehicleNo ?? filters.vehicleNo),
      vehicleMainTypeId:
        (override.vehicleMainTypeId ?? filters.vehicleMainTypeId) &&
        (override.vehicleMainTypeId ?? filters.vehicleMainTypeId) !== "Select"
          ? override.vehicleMainTypeId ?? filters.vehicleMainTypeId
          : "",
      storeId:
        (override.storeId ?? filters.storeId) &&
        (override.storeId ?? filters.storeId) !== "Select"
          ? override.storeId ?? filters.storeId
          : "",
      page: pageOverride,
      pageSize: pageSizeOverride,
    }),
    [filters, page, pageSize]
  );

  /* =========================================================
     API LOADERS
     ========================================================= */
  const loadDropdowns = useCallback(async () => {
    const { data } = await axios.get(`${API_BASE}/init`, {
      params: {
        loginUserId,
        branchId: defaultBranchId || 0,
      },
      headers: commonHeaders,
    });

    if (!data?.success) {
      throw new Error(data?.message || "Failed to load page data");
    }

    setBranches([
      { id: "Select", name: "Select" },
      ...normalizeOptions(data.branches),
    ]);
    setVehicleMainTypes([
      { id: "Select", name: "Select" },
      ...normalizeOptions(data.vehicleMainTypes),
    ]);
    setStores([
      { id: "Select", name: "Select" },
      ...normalizeOptions(data.stores),
    ]);
  }, [loginUserId, defaultBranchId, commonHeaders]);

  const loadList = useCallback(
    async ({
      silent = false,
      overrideFilters = null,
      mode = "search",
      pageOverride = page,
      pageSizeOverride = pageSize,
    } = {}) => {
      if (!canUse(toInt(loginUserId), "loginUserId missing")) return;

      try {
        setGridLoading(true);

        const params =
          mode === "viewall"
            ? {
                branchId: defaultBranchId || "",
                vehicleNo: "",
                vehicleMainTypeId: "",
                storeId: "",
                page: pageOverride,
                pageSize: pageSizeOverride,
              }
            : buildListParams(
                overrideFilters || {},
                pageOverride,
                pageSizeOverride
              );

        const { data } = await axios.get(`${API_BASE}/list`, {
          params,
          headers: commonHeaders,
        });

        if (!data?.success) {
          setRows([]);
          setTotal(0);
          setChecks({});
          if (!silent) toast.warn(data?.message || "No records found");
          return;
        }

        const list = Array.isArray(data.rows) ? data.rows : [];
        setRows(list);
        setTotal(toInt(data.total, list.length));
        resetChecks(list);

        if (!silent) {
          if (list.length === 0) toast.info("No Records Found");
          else toast.success(`Found ${list.length} record(s)`);
        }
      } catch (err) {
        console.error(err);
        setRows([]);
        setTotal(0);
        setChecks({});
        toast.error(
          err?.response?.data?.message || err.message || "Failed to load list"
        );
      } finally {
        setGridLoading(false);
      }
    },
    [
      loginUserId,
      defaultBranchId,
      commonHeaders,
      buildListParams,
      resetChecks,
      canUse,
      page,
      pageSize,
    ]
  );

  const loadInit = useCallback(async () => {
    if (!canUse(toInt(loginUserId), "loginUserId missing")) return;

    try {
      setPageLoading(true);
      setDropdownLoading(true);

      await loadDropdowns();
      await loadList({ silent: true, mode: "viewall", pageOverride: 1 });
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || err.message || "Initialization failed"
      );
    } finally {
      setDropdownLoading(false);
      setPageLoading(false);
    }
  }, [loginUserId, canUse, loadDropdowns, loadList]);

  const fetchVehicleNoSuggestions = useCallback(
    async (prefix) => {
      const pfx = clean(prefix);
      if (!pfx) return [];

      const { data } = await axios.get(`${API_BASE}/autocomplete/vehicleNo`, {
        params: {
          prefix: pfx,
          branchId:
            filters.branchId && filters.branchId !== "Select"
              ? filters.branchId
              : "",
        },
        headers: commonHeaders,
      });

      if (!data?.success) return [];

      const list = data.list || [];
      return Array.isArray(list)
        ? list
            .map((x) =>
              typeof x === "string"
                ? { value: x, label: x }
                : {
                    value: x.value ?? x.id ?? x.name,
                    label: x.label ?? x.name ?? x.value,
                  }
            )
            .filter((x) => toStr(x.value))
        : [];
    },
    [filters.branchId, commonHeaders]
  );

  /* ---------------- handlers ---------------- */
  const onFilterChange = useCallback((e) => {
    const { name, value } = e.target;
    setFilters((p) => ({ ...p, [name]: value }));
  }, []);

  const handleVehicleNoInput = useCallback(
    (value) => {
      setFilters((p) => ({ ...p, vehicleNo: value }));

      if (autocompleteTimer.current) {
        clearTimeout(autocompleteTimer.current);
      }

      const prefix = toStr(value);
      if (!prefix || prefix.length < 1) {
        setVehNoSuggestions([]);
        setShowVehNoSuggestions(false);
        return;
      }

      autocompleteTimer.current = setTimeout(async () => {
        try {
          const items = await fetchVehicleNoSuggestions(prefix);
          setVehNoSuggestions(items.slice(0, 8));
          setShowVehNoSuggestions(items.length > 0);
        } catch {
          setVehNoSuggestions([]);
          setShowVehNoSuggestions(false);
        }
      }, 250);
    },
    [fetchVehicleNoSuggestions]
  );

  const selectVehicleNoSuggestion = useCallback((value) => {
    setFilters((p) => ({ ...p, vehicleNo: value }));
    setVehNoSuggestions([]);
    setShowVehNoSuggestions(false);
  }, []);

  const handleSearch = useCallback(async () => {
    setPage(1);
    await loadList({ silent: false, mode: "search", pageOverride: 1 });
  }, [loadList]);

  const handleViewAll = useCallback(async () => {
    const resetFilters = defaultFiltersFactory(defaultBranchId);
    setFilters(resetFilters);
    resetSuggestions();
    setPage(1);
    await loadList({
      silent: false,
      mode: "viewall",
      pageOverride: 1,
      overrideFilters: resetFilters,
    });
  }, [defaultBranchId, resetSuggestions, loadList]);

  const handleRefresh = useCallback(async () => {
    resetSuggestions();
    setPage(1);
    await loadInit();
  }, [resetSuggestions, loadInit]);

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const openBill = useCallback((row) => {
    const billId = toInt(row?.fld_billid, 0);
    if (!billId) {
      toast.warn("BillID missing");
      return;
    }
    window.open(`/vehicle/BillApprovalEntry?billId=${billId}`, "_blank");
  }, []);

  const setRowApprove = useCallback((billId, approve) => {
    setChecks((prev) => ({
      ...prev,
      [billId]: approve
        ? { approve: true, disapprove: false }
        : { approve: false, disapprove: true },
    }));
  }, []);

  const getChangedItems = useCallback(() => {
    const payloadItems = rows
      .map((r) => toInt(r.fld_billid, 0))
      .filter(Boolean)
      .map((billId) => ({
        billId,
        isApproved: !!checks[billId]?.approve,
      }));

    return payloadItems.filter((x) => {
      const r = rows.find((t) => toInt(t.fld_billid, 0) === x.billId);
      const original = !!r?.fld_IsApproved;
      return original !== !!x.isApproved;
    });
  }, [rows, checks]);

  const performBulk = useCallback(
    async (kind, items) => {
      const changed = Array.isArray(items) ? items : [];
      if (!changed.length) return;

      try {
        setActionLoading(true);

        const url =
          kind === "approval"
            ? `${API_BASE}/approve`
            : `${API_BASE}/disapprove`;

        const { data } = await axios.post(
          url,
          {
            loginUserId: loginUserId || 0,
            items: changed,
          },
          { headers: commonHeaders }
        );

        if (!data?.success) {
          throw new Error(data?.message || "Update failed");
        }

        toast.success(data.message || "Updated successfully");
        setConfirmDlg((p) => ({ ...p, open: false, kind: "", items: [] }));
        await loadList({ silent: true, mode: "search", pageOverride: page });
      } catch (err) {
        console.error(err);
        toast.error(
          err?.response?.data?.message || err.message || "Update failed"
        );
      } finally {
        setActionLoading(false);
      }
    },
    [loginUserId, commonHeaders, loadList, page]
  );

  const postBulk = useCallback(
    (kind) => {
      if (!rows.length) {
        toast.info("No rows to update");
        return;
      }

      const changed = getChangedItems();
      if (changed.length === 0) {
        toast.info("No changes to submit");
        return;
      }

      const isApproval = kind === "approval";
      setConfirmDlg({
        open: true,
        kind,
        title: isApproval ? "Approve Bills" : "Disapprove Bills",
        message: `Submit ${changed.length} change(s) as ${kind.toUpperCase()}?`,
        confirmText: isApproval ? "Approve" : "DisApprove",
        danger: !isApproval,
        items: changed,
      });
    },
    [rows, getChangedItems]
  );

  const handleConfirmOk = useCallback(async () => {
    if (!confirmDlg.kind) return;
    await performBulk(confirmDlg.kind, confirmDlg.items);
  }, [confirmDlg.kind, confirmDlg.items, performBulk]);

  const sendSms = useCallback((row) => {
    const billNo = toStr(row?.fld_billno);
    const billDate = toStr(row?.fld_billdate);
    const mobile = toStr(row?.fld_mobno || row?.fld_mobile || "");

    const msg = `Equipment Bill No : ${billNo},${billDate} Successfully, Nissi Infotech on ${new Date()
      .toLocaleString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
      .replace(",", "")}`;

    window.open(
      `/vehicle/SendSMS?mobile=${encodeURIComponent(
        mobile
      )}&message=${encodeURIComponent(msg)}&billNo=${encodeURIComponent(
        billNo
      )}&billDate=${encodeURIComponent(billDate)}`,
      "_blank"
    );
  }, []);

  const totalPages = useMemo(() => {
    const t = toInt(total, 0);
    return t > 0 ? Math.max(1, Math.ceil(t / pageSize)) : 1;
  }, [total, pageSize]);

  const gotoPage = useCallback(
    async (nextPage) => {
      const safePage = Math.min(Math.max(1, nextPage), totalPages);
      setPage(safePage);
      await loadList({
        silent: true,
        mode: "search",
        pageOverride: safePage,
      });
    },
    [loadList, totalPages]
  );

  /* ---------------- effects ---------------- */
  useEffect(() => {
    loadInit();
  }, [loadInit]);

  useEffect(() => {
    const onDocClick = (ev) => {
      if (!sugBoxRef.current) return;
      if (
        sugBoxRef.current.contains(ev.target) ||
        vehNoRef.current?.contains(ev.target)
      ) {
        return;
      }
      setShowVehNoSuggestions(false);
    };

    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  useEffect(() => {
    return () => {
      if (autocompleteTimer.current) {
        clearTimeout(autocompleteTimer.current);
      }
    };
  }, []);

  useEffect(() => {
    if (page !== 1) {
      setPage(1);
    }

    loadList({
      silent: true,
      mode: "search",
      pageOverride: 1,
      pageSizeOverride: pageSize,
    });
  }, [pageSize]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ---------------- computed ---------------- */
  const canPrev = page > 1;
  const canNext = page < totalPages;
  const recordsCount = useMemo(() => rows.length, [rows]);

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={
            <span className="font-dmserif text-3xl">
              VEHICLE BILL APPROVAL (HO)
            </span>
          }
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div className={cardClass}>
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
              <div className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Equipment Bill Approval
              </div>

              <div className={`text-sm mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Branch ID: {defaultBranchId || "-"} {" • "} User ID: {loginUserId || "-"}
              </div>

              <div className="mt-2 flex flex-wrap gap-2">
                <span className={pillClass}>Records: {total || recordsCount}</span>
                <span className={pillClass}>Branch Type: {branchType || "-"}</span>
                <span className={pillClass}>Page: {page}</span>
              </div>
            </div>

            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setShowFilters((s) => !s)}
                disabled={isBusy}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white"
                    : "bg-gray-200 hover:bg-gray-300 text-gray-700"
                } ${isBusy ? "opacity-70 cursor-not-allowed" : ""}`}
              >
                <FaFilter className="w-4 h-4" />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <button
                onClick={handleRefresh}
                disabled={isBusy}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white"
                    : "bg-gray-200 hover:bg-gray-300 text-gray-700"
                } ${isBusy ? "opacity-70 cursor-not-allowed" : ""}`}
                title="Refresh"
              >
                <FaSyncAlt className="w-4 h-4" />
                Refresh
              </button>
            </div>
          </div>

          {/* Filters */}
          {showFilters && (
            <div className={sectionClass}>
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <FaFilter /> FILTRATION AREA
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Column 1 */}
                <div className="space-y-5">
                  <div>
                    <label className={labelClass}>Branch</label>
                    <select
                      name="branchId"
                      value={filters.branchId}
                      onChange={onFilterChange}
                      className={inputClass}
                      disabled={isBusy}
                    >
                      <option value="Select">Select</option>
                      {branches
                        .filter((x) => x.id !== "Select")
                        .map((b) => (
                          <option key={b.id} value={b.id}>
                            {b.name}
                          </option>
                        ))}
                    </select>
                  </div>

                  <div className="relative">
                    <label className={labelClass}>Vehicle Number</label>
                    <input
                      ref={vehNoRef}
                      name="vehicleNo"
                      value={filters.vehicleNo}
                      onChange={(e) => handleVehicleNoInput(e.target.value)}
                      onFocus={() =>
                        vehNoSuggestions.length > 0 && setShowVehNoSuggestions(true)
                      }
                      className={inputClass}
                      placeholder="Type vehicle no..."
                      maxLength={20}
                      autoComplete="off"
                      disabled={isBusy}
                    />

                    {showVehNoSuggestions && vehNoSuggestions.length > 0 && (
                      <div ref={sugBoxRef} className={suggestionBoxClass}>
                        {vehNoSuggestions.map((s, idx) => (
                          <button
                            type="button"
                            key={`${s.value}-${idx}`}
                            onClick={() => selectVehicleNoSuggestion(s.value)}
                            className={`w-full text-left px-3 py-2 text-sm ${
                              isDark
                                ? "text-gray-100 hover:bg-gray-800"
                                : "text-gray-900 hover:bg-gray-50"
                            }`}
                          >
                            {s.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Column 2 */}
                <div className="space-y-5">
                  <div>
                    <label className={labelClass}>Vehicle Main Type</label>
                    <select
                      name="vehicleMainTypeId"
                      value={filters.vehicleMainTypeId}
                      onChange={onFilterChange}
                      className={inputClass}
                      disabled={isBusy}
                    >
                      <option value="Select">Select</option>
                      {vehicleMainTypes
                        .filter((x) => x.id !== "Select")
                        .map((v) => (
                          <option key={v.id} value={v.id}>
                            {v.name}
                          </option>
                        ))}
                    </select>
                  </div>

                  <div>
                    <label className={labelClass}>Store</label>
                    <select
                      name="storeId"
                      value={filters.storeId}
                      onChange={onFilterChange}
                      className={inputClass}
                      disabled={isBusy}
                    >
                      <option value="Select">Select</option>
                      {stores
                        .filter((x) => x.id !== "Select")
                        .map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.name}
                          </option>
                        ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex flex-col md:flex-row gap-3 justify-between items-center mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={resetFiltersOnly}
                  disabled={isBusy}
                  className={`flex items-center gap-2 ${
                    isDark
                      ? "text-gray-300 hover:text-white"
                      : "text-gray-600 hover:text-gray-900"
                  } font-medium transition-colors duration-200 ${
                    isBusy ? "opacity-60 cursor-not-allowed" : ""
                  }`}
                >
                  <FaUndo />
                  Clear Filters
                </button>

                <div className="flex gap-3">
                  <button
                    onClick={handleSearch}
                    disabled={isBusy}
                    className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-60`}
                  >
                    <FaSearch />
                    {gridLoading ? "Searching..." : "Search"}
                  </button>

                  <button
                    onClick={handleViewAll}
                    disabled={isBusy}
                    className="flex items-center gap-2 px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-lg font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-60"
                  >
                    <FaEye />
                    View All
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* GRID */}
          <div className="mt-8">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-3">
              <div className={`text-lg font-semibold ${isDark ? "text-white" : "text-gray-800"}`}>
                Vehicle Bill Approval List
              </div>

              <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Records: <span className="font-semibold">{total}</span>
                {" • "}
                Current Page: <span className="font-semibold">{page}</span>
              </div>
            </div>

            <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
              <table className="min-w-[1200px] w-full text-sm">
                <thead className={`${buttonClass} text-white`}>
                  <tr>
                    <th className="px-4 py-3 text-center">S.No</th>
                    <th className="px-4 py-3 text-center">BillID</th>
                    <th className="px-4 py-3 text-center">BillNo</th>
                    <th className="px-4 py-3 text-center">Vehicle Main Type</th>
                    <th className="px-4 py-3 text-center">Vehicle Number</th>
                    <th className="px-4 py-3 text-center">Date</th>
                    <th className="px-4 py-3 text-center">Branch Name</th>
                    <th className="px-4 py-3 text-center">Vehicle Name</th>
                    <th className="px-4 py-3 text-center">Vehicle Type</th>
                    <th className="px-4 py-3 text-center">Company Name</th>
                    <th className="px-4 py-3 text-center">Approve</th>
                    <th className="px-4 py-3 text-center">DisApprove</th>
                    <th className="px-4 py-3 text-center">SendSMS</th>
                  </tr>
                </thead>

                <tbody className={isDark ? "bg-gray-900 text-gray-100" : "bg-white text-gray-800"}>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={13} className="px-4 py-10 text-center text-gray-500">
                        {gridLoading || pageLoading ? "Loading..." : "No records found"}
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => {
                      const billId = toInt(r.fld_billid, 0);
                      const c = checks[billId] || {
                        approve: false,
                        disapprove: false,
                      };

                      return (
                        <tr
                          key={billId || idx}
                          className={
                            isDark
                              ? "border-t border-gray-800 hover:bg-gray-800/40"
                              : "border-t border-gray-100 hover:bg-gray-50"
                          }
                        >
                          <td className="px-4 py-3 text-center">
                            {(page - 1) * pageSize + idx + 1}
                          </td>
                          <td className="px-4 py-3 text-center">{r.fld_billid || "-"}</td>

                          <td className="px-4 py-3 text-center">
                            <button
                              type="button"
                              onClick={() => openBill(r)}
                              className={`underline font-semibold ${
                                isDark
                                  ? "text-blue-300 hover:text-blue-200"
                                  : "text-blue-700 hover:text-blue-900"
                              }`}
                            >
                              {r.fld_billno || "-"}
                            </button>
                          </td>

                          <td className="px-4 py-3 text-center">{r.VehicleType || "-"}</td>
                          <td className="px-4 py-3 text-center">{r.fld_vehicle_regno || "-"}</td>
                          <td className="px-4 py-3 text-center">{r.fld_billdate || "-"}</td>
                          <td className="px-4 py-3 text-center">{r.fld_BranchName || "-"}</td>
                          <td className="px-4 py-3 text-center">{r.fld_vehicle_name || "-"}</td>
                          <td className="px-4 py-3 text-center">
                            {r.fld_vehicle_subtypename || "-"}
                          </td>
                          <td className="px-4 py-3 text-center">{r.fld_companyname || "-"}</td>

                          <td className="px-4 py-3 text-center">
                            <input
                              type="checkbox"
                              checked={!!c.approve}
                              onChange={(e) =>
                                setRowApprove(billId, e.target.checked)
                              }
                              className="w-4 h-4"
                              disabled={isBusy}
                            />
                          </td>

                          <td className="px-4 py-3 text-center">
                            <input
                              type="checkbox"
                              checked={!!c.disapprove}
                              onChange={(e) =>
                                setRowApprove(
                                  billId,
                                  !e.target.checked ? true : false
                                )
                              }
                              className="w-4 h-4"
                              disabled={isBusy}
                            />
                          </td>

                          <td className="px-4 py-3 text-center">
                            <button
                              type="button"
                              onClick={() => sendSms(r)}
                              className={`underline ${
                                isDark
                                  ? "text-emerald-300 hover:text-emerald-200"
                                  : "text-emerald-700 hover:text-emerald-900"
                              }`}
                              disabled={isBusy}
                            >
                              SendSMS
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>

                {rows.length > 0 && (
                  <tfoot className={isDark ? "bg-gray-900" : "bg-white"}>
                    <tr
                      className={`border-t ${
                        isDark ? "border-gray-800" : "border-gray-200"
                      }`}
                    >
                      <td colSpan={10} className="px-4 py-3 text-sm">
                        <div className={isDark ? "text-gray-300" : "text-gray-600"}>
                          Bulk actions
                        </div>
                      </td>

                      <td className="px-4 py-3 text-center">
                        <button
                          type="button"
                          disabled={isBusy}
                          onClick={() => postBulk("approval")}
                          className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-white shadow ${
                            isDark
                              ? "bg-green-700 hover:bg-green-600"
                              : "bg-green-600 hover:bg-green-700"
                          } disabled:opacity-60`}
                        >
                          <FaCheckCircle />
                          Approve
                        </button>
                      </td>

                      <td className="px-4 py-3 text-center">
                        <button
                          type="button"
                          disabled={isBusy}
                          onClick={() => postBulk("disapproval")}
                          className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-white shadow ${
                            isDark
                              ? "bg-red-700 hover:bg-red-600"
                              : "bg-red-600 hover:bg-red-700"
                          } disabled:opacity-60`}
                        >
                          <FaTimesCircle />
                          DisApprove
                        </button>
                      </td>

                      <td className="px-4 py-3" />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>

            {/* Paging */}
            {rows.length > 0 && (
              <div
                className={`flex flex-col md:flex-row items-center justify-between gap-3 px-4 py-3 border rounded-b-xl ${
                  isDark
                    ? "border-gray-700 bg-gray-900"
                    : "border-gray-200 bg-white"
                }`}
              >
                <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Page {page} of {totalPages} • Total {total}
                </div>

                <div className="flex items-center gap-2">
                  <select
                    value={pageSize}
                    onChange={(e) => setPageSize(toInt(e.target.value, 10))}
                    className={`px-2 py-1 rounded border ${
                      isDark
                        ? "bg-gray-900 border-gray-700 text-gray-200"
                        : "bg-white border-gray-300 text-gray-800"
                    }`}
                    disabled={isBusy}
                  >
                    {[10, 20, 50, 100].map((n) => (
                      <option key={n} value={n}>
                        {n}/page
                      </option>
                    ))}
                  </select>

                  <button
                    type="button"
                    disabled={!canPrev || isBusy}
                    onClick={() => gotoPage(page - 1)}
                    className={`px-3 py-1 rounded border ${
                      isDark
                        ? "border-gray-700 text-gray-200 hover:bg-gray-800"
                        : "border-gray-300 text-gray-800 hover:bg-gray-50"
                    } disabled:opacity-50`}
                  >
                    Prev
                  </button>

                  <button
                    type="button"
                    disabled={!canNext || isBusy}
                    onClick={() => gotoPage(page + 1)}
                    className={`px-3 py-1 rounded border ${
                      isDark
                        ? "border-gray-700 text-gray-200 hover:bg-gray-800"
                        : "border-gray-300 text-gray-800 hover:bg-gray-50"
                    } disabled:opacity-50`}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}

            {isBusy && (
              <div className={`mt-3 text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                {pageLoading
                  ? "Loading page..."
                  : dropdownLoading
                  ? "Loading dropdown..."
                  : gridLoading
                  ? "Loading grid..."
                  : actionLoading
                  ? "Processing action..."
                  : "Loading..."}
              </div>
            )}
          </div>

          {/* Back */}
          <div className="flex justify-start mt-6">
            <button
              onClick={handleBack}
              disabled={isBusy}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                isDark
                  ? "bg-gray-700 hover:bg-gray-600 text-white"
                  : "bg-gray-200 hover:bg-gray-300 text-gray-700"
              } ${isBusy ? "opacity-70 cursor-not-allowed" : ""}`}
            >
              <FaArrowLeft />
              Back
            </button>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={2500}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>

      <ConfirmDialog
        open={confirmDlg.open}
        isDark={isDark}
        title={confirmDlg.title}
        message={confirmDlg.message}
        confirmText={actionLoading ? "Processing..." : confirmDlg.confirmText}
        cancelText="Cancel"
        danger={confirmDlg.danger}
        confirmClass={
          confirmDlg.danger
            ? "flex items-center gap-2 px-5 py-3 rounded-lg font-semibold shadow-lg text-white bg-red-600 hover:bg-red-700"
            : "flex items-center gap-2 px-5 py-3 rounded-lg font-semibold shadow-lg text-white bg-green-600 hover:bg-green-700"
        }
        ghostClass={ghostBtn}
        busy={isBusy}
        onCancel={closeConfirm}
        onConfirm={handleConfirmOk}
      />
    </VehicleLayoutWrapper>
  );
};

export default VehicleBillApproval;